import { useEffect, useRef } from "react";
import { getPendingLeave, getResetVersion, requestLeave } from "shared/helpers/unsavedChanges.store";

/**
 * Actions waiting to be replayed after **Exit Without Saving** remounted the component that asked
 * for them (KP-2453).
 *
 * Module scope on purpose: `resetAll` bumps the store's `resetVersion`, which `MerchantSetting` uses
 * as the `<Tabs>` key, so the component holding the request is already on its way out — neither
 * state nor a ref would survive to run the action. Keyed by `actionId` because every guarded
 * component remounts at once, and a shared slot would let the first mount effect steal another
 * component's action.
 */
const parkedActions = new Map<string, unknown>();

/** The fields an action is blocked on, plus the popup wording that names them. */
export interface GuardedActionFields {
  /** Form field names that block the action. Widen the guard by adding a name here. */
  fields: string[];
  /** Popup body copy. Omit to keep the generic page-wide wording. */
  description?: string;
}

/**
 * Gate an in-page action behind the unsaved-changes popup whenever one of `fieldNames` is unsaved
 * (KP-2453). For actions that stay on the page but write to the server against a setting the form
 * has not saved yet — the Transaction Fee popups, which would store a fee record against the
 * surcharge setting the server still holds rather than the one on screen.
 *
 * Everything else the user has unsaved is ignored, so a dirty payout currency never blocks the
 * action and is never highlighted.
 *
 * The three popup answers behave as: **Cancel** drops the action and keeps the user here,
 * **Save And Exit** saves every dirty form then runs it, **Exit Without Saving** restores the
 * original values and replays it after the remount that causes.
 *
 * ```tsx
 * const requestOpenFee = useGuardedAction<FeeAction>("transaction-fee", TRANSACTION_FEE_GUARD, (action) =>
 *   action.type === "edit" ? openEditForm(action.id) : openCreateForm()
 * );
 * ```
 *
 * @param actionId unique key for this action; must differ from every other guarded action that can
 * be mounted at the same time
 * @param guard which fields block it, and the popup copy naming them
 * @param run performs the action; read through a ref, so an inline arrow is fine
 * @returns the handler to call from the button or menu item, carrying whatever `run` needs
 */
const useGuardedAction = <TPayload = void>(
  actionId: string,
  guard: GuardedActionFields,
  run: (payload: TPayload) => void
) => {
  // Held in a ref so the mount effect does not depend on a handler rebuilt on every render.
  const runRef = useRef(run);
  runRef.current = run;

  useEffect(() => {
    // Picks up the action the unmounted instance left behind. Nothing is parked on a normal mount,
    // so this is a no-op then.
    if (!parkedActions.has(actionId)) return;

    const payload = parkedActions.get(actionId) as TPayload;
    parkedActions.delete(actionId);
    runRef.current(payload);
  }, [actionId]);

  return (payload: TPayload) => {
    // Another prompt is already asking, so this click would park an intent nothing ever resolves.
    if (getPendingLeave()) return;

    const resetVersionBefore = getResetVersion();
    parkedActions.set(actionId, payload);
    requestLeave(
      () => {
        // Exit Without Saving remounts the caller in this very commit, so running the action here
        // would set state on a subtree React is about to throw away. A `resetVersion` that moved is
        // exactly that case: leave the action parked for the fresh instance's mount effect.
        if (getResetVersion() !== resetVersionBefore) return;

        parkedActions.delete(actionId);
        run(payload);
      },
      {
        fieldNames: guard.fields,
        description: guard.description,
        // The user chose to stay, so the action must not resurface on some later reset.
        onCancel: () => {
          parkedActions.delete(actionId);
        },
      }
    );
  };
};

export default useGuardedAction;
